import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal, TextInput, Stack, Text, Button, Group, ScrollArea, UnstyledButton, Box,
} from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import agentMutations from '@/api/mutitations/agent';
import intakeEventMutations from '@/api/mutitations/intakeEvent';
import { routeWithParams } from '@/helpers/route';
import routes from '@/routes';
import prepareTranslate from '@/helpers/dictionary';

const ReassignAgentModal = ({
  opened, onClose, event, onSuccess,
}) => {
  const navigate = useNavigate();
  const t = prepareTranslate();
  const [agents, setAgents] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState(null);

  const { mutateAsync: getAgents } = agentMutations.useGetAgents();
  const { mutateAsync: reassign, isPending } = agentMutations.useReassignAutoAgent();
  const { mutateAsync: retryFile } = intakeEventMutations.useRetryFilename();

  useEffect(() => {
    if (!opened) return;
    const fetchAgents = async () => {
      try {
        const result = await getAgents({ start: 1, end: 200, query: { status: 'active' } });
        setAgents(result?.data || []);
      } catch {
        notifications.show({
          title: t('components.reassignAgentModal.notification.fetchError.title'),
          message: t('components.reassignAgentModal.notification.fetchError.message'),
        });
      }
    };
    fetchAgents();
    setSearch('');
    setSelectedAgentId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const filtered = agents.filter((a) => {
    const fullName = `${a.personalDetails?.firstName || ''} ${a.personalDetails?.lastName || ''}`;
    return fullName.includes(search) || a.personalDetails?.uid?.includes(search);
  });

  const handleConfirm = async () => {
    if (!selectedAgentId) return;
    try {
      await reassign({
        newAgentId: event.relatedEntity?.entityId,
        existingAgentId: selectedAgentId,
        intakeEventId: event._id,
      });
      if (event.fileRef?.s3Key) {
        await retryFile({ intakeEventId: event._id }).catch(() => {});
      }
      onSuccess();
      navigate(routeWithParams(routes.agentView.path, { id: selectedAgentId, tab: 'details' }));
    } catch {
      notifications.show({
        title: t('components.reassignAgentModal.notification.reassignError.title'),
        message: t('components.reassignAgentModal.notification.reassignError.message'),
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('components.reassignAgentModal.title')}
      centered
    >
      <Stack gap="md">
        <Text size="sm">{t('components.reassignAgentModal.subtitle')}</Text>
        <TextInput
          placeholder={t('components.reassignAgentModal.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
        <ScrollArea h={300}>
          <Stack gap="xs">
            {filtered.map((agent) => {
              const fullName = `${agent.personalDetails?.firstName || ''} ${agent.personalDetails?.lastName || ''}`;
              const isSelected = agent._id === selectedAgentId;
              return (
                <UnstyledButton
                  key={agent._id}
                  onClick={() => setSelectedAgentId(agent._id)}
                >
                  <Box
                    p="sm"
                    style={{
                      borderRadius: 8,
                      border: `1px solid ${isSelected ? 'var(--mantine-color-blue-6)' : 'var(--mantine-color-gray-3)'}`,
                      backgroundColor: isSelected ? 'var(--mantine-color-blue-0)' : 'transparent',
                    }}
                  >
                    <Text size="sm" fw={isSelected ? 600 : 400}>{fullName}</Text>
                    <Text size="xs" c="dimmed">{agent.personalDetails?.uid}</Text>
                  </Box>
                </UnstyledButton>
              );
            })}
          </Stack>
        </ScrollArea>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('components.reassignAgentModal.actions.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedAgentId}
            loading={isPending}
          >
            {t('components.reassignAgentModal.actions.confirm')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

ReassignAgentModal.propTypes = {
  opened: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  event: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    relatedEntity: PropTypes.shape({
      entityId: PropTypes.string,
    }),
    fileRef: PropTypes.shape({
      s3Key: PropTypes.string,
    }),
  }).isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default ReassignAgentModal;
